import Button from "@edaz/edazkit/Button/index";
import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import { IconType } from "@edaz/edazkit/Icon";
import { Input } from "@frui.ts/bootstrap";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import DatePickerBox from "controls/datePickerBox";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import type DistributorIssuedPaymentRequestResponse from "entities/distributorIssuedPaymentRequestResponse";
import DistributorsPaymentRequestStatus from "entities/distributorPaymentRequestStatus";
import { observer } from "mobx-react-lite";
import React from "react";
import Col from "react-bootstrap/Col";
import { busyIdentificator } from "utils/busyWatcher";
import { formatAmount, formatDate, formatNumber, translateGeneral } from "utils/localizations";
import DistributorPaymentRequestReportsViewModel from "viewModels/timeCharges/distributors/distributorPaymentRequestReports/distributorPaymentRequestReports";
import FilterView from "../../../common/filter";
import ListView from "../../../common/list";
import { emptyValue } from "utils/emptyValue";

const DistributorPaymentRequestReportsView: ViewComponent<DistributorPaymentRequestReportsViewModel> = observer(({ vm }) => {
  const columns: ColumnDefinition<DistributorIssuedPaymentRequestResponse>[] = [
    {
      property: "validSinceDate",
      title: vm.translateAttribute("validSinceDate"),
      valueFormatter: props => formatDate(props.value) || emptyValue,
    },
    {
      property: "validUntilDate",
      title: vm.translateAttribute("validUntilDate"),
      valueFormatter: props => formatDate(props.value),
    },
    {
      property: "dueDate",
      title: vm.translateAttribute("dueDate"),
      valueFormatter: props => formatDate(props.value),
    },
    {
      property: "variableSymbol",
      title: vm.translateAttribute("variableSymbol"),
    },
    {
      property: "totalAmount",
      title: vm.translateAttribute("totalAmount"),
      valueFormatter: props => formatAmount(props.value),
    },
    {
      property: "paidAmount",
      title: vm.translateAttribute("paidAmount"),
      valueFormatter: props => formatAmount(props.value),
    },
    {
      title: vm.translateAttribute("status"),
      valueFormatter: props => vm.getPaymentRequestStatus(props.item),
    },
    {
      property: "licensePlatesCount",
      title: vm.translateAttribute("licensePlatesCount"),
      valueFormatter: props => formatNumber(props.value),
    },
    {
      property: "type",
      title: vm.translateAttribute("type"),
      valueFormatter: props => vm.translatePaymentRequestType(props.value),
    },
    {
      property: "requestMessage",
      title: vm.translateAttribute("requestMessage"),
      valueFormatter: props => props.value || emptyValue,
    },
  ];

  const actionButtons = (item: DistributorIssuedPaymentRequestResponse) => {
    const isBusy = vm.busyWatcher.checkBusy(busyIdentificator([item]));
    return (
      <Button
        variant="success"
        className="rounded"
        size="sm"
        iconLeft={isBusy ? IconType.Spinner : IconType.Download}
        disabled={isBusy}
        onClick={() => vm.onDownloadClick(item)}>
        {vm.translate("detail")}
      </Button>
    );
  };

  let headerButton;
  const distributorId = vm.distributorId;
  if (distributorId) {
    const isBusy = vm.busyWatcher.checkBusy(busyIdentificator([distributorId]));
    headerButton = (
      <Button
        variant="primary"
        size="sm"
        className="rounded"
        iconLeft={isBusy ? IconType.Spinner : IconType.Download}
        disabled={isBusy}
        onClick={() => vm.onReportClick(distributorId)}>
        {vm.translate("report")}
      </Button>
    );
  }

  const filters = (
    <FilterView vm={vm}>
      <div className="row">
        <FormGroup as={Col} md={6} xl={3} target={vm.filter} property="distributorId">
          <FormLabel>{vm.translate("filters.distributorId")}</FormLabel>
          <AdvancedSelect items={vm.distributorOptions} placeholder={translateGeneral("select")} />
        </FormGroup>
        <FormGroup as={Col} md={6} xl={3} target={vm.filter} property="validSinceDate">
          <FormLabel>{vm.translate("filters.validSince")}</FormLabel>
          <DatePickerBox placeholder={translateGeneral("search")} />
        </FormGroup>
        <FormGroup as={Col} md={6} xl={3} target={vm.filter} property="validUntilDate">
          <FormLabel>{vm.translate("filters.validUntil")}</FormLabel>
          <DatePickerBox placeholder={translateGeneral("search")} />
        </FormGroup>
        <FormGroup as={Col} md={6} xl={3} target={vm.filter} property="variableSymbol">
          <FormLabel>{vm.translate("filters.variableSymbol")}</FormLabel>
          <Input placeholder={translateGeneral("search")} />
        </FormGroup>
      </div>
    </FilterView>
  );

  const rowProps = (item: DistributorIssuedPaymentRequestResponse) => {
    if (item.status === DistributorsPaymentRequestStatus.AfterDueDate) {
      return {
        className: "text-danger",
      };
    }

    if (item.status === DistributorsPaymentRequestStatus.PartiallyPaid) {
      return {
        className: "text-warning",
      };
    }

    return {};
  };

  return (
    <ListView
      columns={columns}
      vm={vm}
      headerButtons={headerButton}
      aboveTable={filters}
      actionButtons={actionButtons}
      mandatoryFilter={true}
      rowProps={rowProps}
    />
  );
});

registerView(DistributorPaymentRequestReportsView, DistributorPaymentRequestReportsViewModel);
